/**
 * Fishbone (Ishikawa) ponderado — SVG inline theme-aware (não recharts).
 *
 * Regras duras do brief:
 *  - 6 espinhas SEMPRE (3 em cima, 3 embaixo), comprimento e stroke-width
 *    ∝ ao peso da categoria; categoria sem dado = espinha mínima + badge
 *    neutral "0 · sem dados" (nunca sumida, nunca todas iguais com dado).
 *  - Cor da espinha = COR_CATEGORIA (o MESMO tom em todos os blocos).
 *  - Categoria dominante realçada; clique na espinha cross-filtra
 *    (`onCategoriaClick`), com feedback visual quando há filtro ativo.
 *  - Sub-espinhas = top sub-causas da categoria, escaladas pelo peso.
 */
import React from "react";

import { COR_CATEGORIA } from "../categorias6m";
import { fmtNum } from "../_shared/ui";

const LARGURA = 940;
const ALTURA = 410;
const Y_ESPINHA = 205;
const X_INICIO = 28;
const X_FIM = 760;
const COMPRIMENTO_MIN = 55;
const COMPRIMENTO_EXTRA = 115;

// Posições fixas (3 em cima / 3 embaixo) na ordem canônica das 6M.
const POSICOES = [
  { lado: "cima", xBase: 250 },
  { lado: "cima", xBase: 470 },
  { lado: "cima", xBase: 690 },
  { lado: "baixo", xBase: 250 },
  { lado: "baixo", xBase: 470 },
  { lado: "baixo", xBase: 690 },
];

function truncar(texto, max = 22) {
  const s = String(texto || "");
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function Espinha({
  codigo, label, peso, pct, subCausas, posicao, maxPeso, dominante,
  ativa, algumaAtiva, formatPeso, onClick,
}) {
  const cor = COR_CATEGORIA[codigo] || "var(--neutral-solid)";
  const temDado = peso > 0;
  const ratio = maxPeso > 0 ? peso / maxPeso : 0;
  const comprimento = COMPRIMENTO_MIN + ratio * COMPRIMENTO_EXTRA;
  const dx = comprimento * 0.55;
  const dy = comprimento * 0.84;
  const sobe = posicao.lado === "cima";
  const yTip = sobe ? Y_ESPINHA - dy : Y_ESPINHA + dy;
  const xTip = posicao.xBase - dx;
  const strokeW = 2 + ratio * 5;
  const corTraco = temDado ? cor : "var(--border-strong)";

  const yLabel = sobe ? yTip - 26 : yTip + 24;
  const yBadge = sobe ? yTip - 12 : yTip + 38;

  const maxSub = Math.max(...(subCausas || []).map((s) => s.peso), 1);

  return (
    <g
      onClick={() => onClick(codigo)}
      style={{ cursor: "pointer" }}
      opacity={algumaAtiva && !ativa ? 0.4 : 1}
      role="button"
      aria-label={`${label}: ${formatPeso(peso)} (${fmtNum(pct)}%)`}
    >
      <title>
        {temDado
          ? `${label} — ${formatPeso(peso)} · ${fmtNum(pct)}% do total. Clique para filtrar.`
          : `${label} — sem ocorrências no recorte.`}
      </title>
      {/* espinha */}
      <line
        x1={posicao.xBase}
        y1={Y_ESPINHA}
        x2={xTip}
        y2={yTip}
        stroke={corTraco}
        strokeWidth={strokeW}
        strokeLinecap="round"
      />
      {dominante && temDado && (
        <circle cx={xTip} cy={yTip} r={5} fill={cor} />
      )}
      {/* sub-espinhas (top sub-causas) */}
      {(subCausas || []).slice(0, 3).map((sub, i) => {
        const f = 0.38 + i * 0.24;
        const px = posicao.xBase - dx * f;
        const py = sobe ? Y_ESPINHA - dy * f : Y_ESPINHA + dy * f;
        const len = 20 + (sub.peso / maxSub) * 44;
        return (
          <g key={`${codigo}-sub-${i}`}>
            <title>{`${sub.rotulo} — ${formatPeso(sub.peso)}`}</title>
            <line
              x1={px}
              y1={py}
              x2={px - len}
              y2={py}
              stroke={cor}
              strokeWidth={1.4}
              opacity={0.8}
            />
            <text
              x={px - len - 4}
              y={py + 3}
              textAnchor="end"
              fontSize={9}
              fill="var(--ink-3)"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {truncar(sub.rotulo, 20)}
            </text>
          </g>
        );
      })}
      {/* rótulo + badge de peso */}
      <text
        x={xTip}
        y={yLabel}
        textAnchor="middle"
        fontSize={12}
        fontWeight={dominante ? 700 : 600}
        fill={dominante && temDado ? cor : "var(--ink-2)"}
        textDecoration={ativa ? "underline" : undefined}
        style={{ fontFamily: "var(--font-body)" }}
      >
        {label}
      </text>
      <text
        x={xTip}
        y={yBadge}
        textAnchor="middle"
        fontSize={10.5}
        fill={temDado ? "var(--ink-3)" : "var(--ink-4)"}
        style={{ fontFamily: "var(--font-mono)" }}
      >
        {temDado ? `${formatPeso(peso)} · ${fmtNum(pct)}%` : "0 · sem dados"}
      </text>
    </g>
  );
}

/**
 * @param categorias  [{codigo, label, peso, pct, subCausas:[{rotulo, peso}]}]
 *                    SEMPRE as 6, na ordem canônica.
 * @param rotuloCabeca texto curto da cabeça (recorte + lente).
 * @param formatPeso   (peso) => string (ex.: "1.640 min" | "9").
 */
export default function Fishbone({
  categorias, rotuloCabeca, formatPeso, categoriaAtiva, onCategoriaClick,
}) {
  const maxPeso = Math.max(...categorias.map((c) => c.peso), 0);
  const dominante =
    maxPeso > 0 ? categorias.find((c) => c.peso === maxPeso)?.codigo : null;

  return (
    <svg
      viewBox={`0 0 ${LARGURA} ${ALTURA}`}
      width="100%"
      role="img"
      aria-label={`Diagrama de Ishikawa — ${rotuloCabeca}`}
      style={{ display: "block", maxHeight: 430 }}
    >
      {/* espinha central + seta */}
      <line
        x1={X_INICIO}
        y1={Y_ESPINHA}
        x2={X_FIM}
        y2={Y_ESPINHA}
        stroke="var(--ink-3)"
        strokeWidth={3}
        strokeLinecap="round"
      />
      <polygon
        points={`${X_FIM},${Y_ESPINHA - 7} ${X_FIM + 12},${Y_ESPINHA} ${X_FIM},${Y_ESPINHA + 7}`}
        fill="var(--ink-3)"
      />
      {/* cabeça (recorte + lente) */}
      <rect
        x={X_FIM + 16}
        y={Y_ESPINHA - 30}
        width={LARGURA - X_FIM - 22}
        height={60}
        rx={10}
        fill="var(--surface-2)"
        stroke="var(--border)"
      />
      <text
        x={X_FIM + 16 + (LARGURA - X_FIM - 22) / 2}
        y={Y_ESPINHA + 4}
        textAnchor="middle"
        fontSize={11.5}
        fontWeight={600}
        fill="var(--ink)"
        style={{ fontFamily: "var(--font-body)" }}
      >
        <title>{rotuloCabeca}</title>
        {truncar(rotuloCabeca, 24)}
      </text>

      {categorias.map((cat, idx) => (
        <Espinha
          key={cat.codigo}
          codigo={cat.codigo}
          label={cat.label}
          peso={cat.peso}
          pct={cat.pct}
          subCausas={cat.subCausas}
          posicao={POSICOES[idx]}
          maxPeso={maxPeso}
          dominante={cat.codigo === dominante}
          ativa={categoriaAtiva === cat.codigo}
          algumaAtiva={Boolean(categoriaAtiva)}
          formatPeso={formatPeso}
          onClick={onCategoriaClick}
        />
      ))}
    </svg>
  );
}
